/**
 * usePricing - 套餐实时价格 hook。
 *
 * 拉取 GET /api/pricing（后端从 Creem 读取，KV 缓存 1 小时）。
 * 价格在一次会话内不变，故用模块级缓存避免重复请求。
 * 失败时返回 null，调用方用硬编码兜底价（不阻塞渲染）。
 */

import { useState, useEffect } from "react";
import { getPricing, type PricingResponse } from "../lib/api";
import i18n from "../i18n";

export interface UsePricingResult {
  pricing: PricingResponse | null;
  loading: boolean;
  error: string | null;
}

/** 模块级缓存：同会话只请求一次 */
let cachedPricing: PricingResponse | null = null;

export function usePricing(): UsePricingResult {
  const [pricing, setPricing] = useState<PricingResponse | null>(cachedPricing);
  const [loading, setLoading] = useState(cachedPricing === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedPricing) {
      setPricing(cachedPricing);
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPricing()
      .then((data) => {
        if (cancelled) return;
        cachedPricing = data;
        setPricing(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        // 价格拉取失败不阻塞：返回 null，调用方用硬编码兜底
        setError(err instanceof Error ? err.message : i18n.t("errors.loadPricing"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { pricing, loading, error };
}

/** 仅供测试重置模块级缓存 */
export function __resetPricingCacheForTests(): void {
  cachedPricing = null;
}
